import React, { useState, useEffect } from "react"
import { Search, Info, CopyIcon } from "lucide-react"
import Button from "core/components/Button"
import Input from "core/components/Input"
import { Table } from "core/components/v2/Table"
import { TableBody } from "core/components/v2/Table"
import Checkbox from "core/components/Checkbox"
import BatchChangeViewModel from "../BatchChangeViewModel"
import { TableRow, TableCell } from "@mui/material"
import { useNavigate, useParams } from "react-router-dom"
import DashboardLayout from "core/layouts/DashboardLayout"
import Toast from "core/components/Toast"
import CopyIconV2 from "assets/svgs/v2/CopyIcon"
import { STR_SUCCESS } from "core/constants/strings"

export default function StudentListPage() {
  const navigate = useNavigate()
  const { id } = useParams()

  const {
    loading,
    toast,
    selectedStudents,
    batchList,
    batchStudentDetails,
    selectBatch,
    selectAll,
    emailFilter,
    filteredStudents,
    statusFilter,
    handleSelectAll,
    setStatusFilter,
    setEmailFilter,
    changeToastVisibility,
    fetchBatchStudentDetails,
    getBatches,
    setSelectBatch,
    updateStudentBatch,
    handleClearAll,
    handleSelectStudent,
    getStatusColor,
    changeToastDetails,
  } = BatchChangeViewModel()

  useEffect(() => {
    if (id) {
      fetchBatchStudentDetails(id)
      getBatches()
    }
  }, [id])

  return (
    <DashboardLayout>
      <div className="relative w-[795px] pl-[24px]">
        <div className="flex justify-between items-start w-[747px] mt-6">
          <div>
            <h1 className="text-xl mt-0 font-medium">Student List</h1>
            <p className="text-sm text-muted-foreground">Manage all students for the selected batch!</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex px-4 py-1.5 justify-center items-center gap-3 rounded border border-gray-400 bg-white text-[#646464] text-sm"
          >
            Go Back
          </button>
        </div>

        <div className="flex flex-wrap w-[747px] items-start gap-4 mt-6">
          <div className="relative flex items-center w-[300px] h-[33px] border border-gray-400 bg-white rounded">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Email Id"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              className="w-full h-full pl-9 bg-transparent border-none outline-none text-gray-700 text-[14px]"
            />
          </div>

          <div className="relative flex items-center w-[300px] h-[33px] border border-gray-400 bg-white rounded">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-[300px] h-[33px] pl-4 py-0 bg-transparent border-none outline-none text-[#646464] text-[14px]"
            >
              <option value="">Select Status</option>
              <option value="Active">Active</option>
              <option value="Paused">Paused</option>
              <option value="Opt-Out">Opt-Out</option>
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-4 items-start w-[747px] h-auto p-4 rounded-lg border border-gray-300 bg-white shadow-md mt-6">
          <div className="w-full">
            <h2 className="text-[18px] font-medium text-gray-600 font-inter">Select Student to Merge</h2>
            <div className="flex items-center justify-between w-full gap-4">
              <select
                className="w-[300px] h-[33px] px-4 py-0 border border-gray-400 rounded bg-white text-[#646464] flex justify-center font-inter text-[14px] font-normal leading-normal"
                value={selectBatch}
                onChange={(e) => setSelectBatch(e.target.value)}
              >
                <option value="" disabled>
                  Select Batch
                </option>
                {batchList.map((batch: any, index: any) => (
                  <option key={index} value={batch}>
                    {batch}
                  </option>
                ))}
              </select>
              <button
                className={`w-[138px] h-[35px] px-4 py-2 rounded text-[var(--flat-white,#FFF)] font-inter text-[14px] font-medium leading-normal ${
                  selectedStudents.length === 0 || loading
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-[#162456] text-white cursor-pointer"
                }`}
                onClick={() => updateStudentBatch(true)}
                disabled={selectedStudents.length === 0 || loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Merging...
                  </div>
                ) : (
                  "Merge Selected"
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4 w-full">
            <div className="flex items-center gap-2">
              <Checkbox checked={selectAll} onChange={handleSelectAll} />
              <span>Select All</span>
            </div>
            <button
              onClick={handleClearAll}
              className="flex px-4 py-1.5 justify-center items-center gap-3 rounded border border-gray-400 bg-white text-[#646464] text-sm"
            >
              Clear All
            </button>
          </div>

          <div className="w-full px-[12px] pt-[5px] py-[5px] space-x-[24px] bg-gray-100 rounded-lg justify-between text-sm text-gray-500 font-inter">
            <span>
              Total Students:{" "}
              <strong className="text-green-600">
                {Array.isArray(batchStudentDetails)
                  ? batchStudentDetails.length
                  : Object.keys(batchStudentDetails).length}
              </strong>
            </span>
            <span>
              Selected Students: <strong className="text-green-600">{selectedStudents.length}</strong>
            </span>
          </div>
        </div>
        <div className="border rounded-md overflow-hidden w-[747px] mt-6">
          <div className="bg-gray-800 text-white py-1 px-4 flex h-[31px]">
            <div className="w-8"></div>
            <div className="flex-1">Class Name</div>
            <div className="w-32 text-center">Batch Change</div>
            <div className="w-32 text-center">Status</div>
          </div>
          <div>
            <Table>
              <TableBody>
                {filteredStudents.map((student) => (
                  <TableRow key={student.email}>
                    <TableCell className="w-8 p-0 pl-4 align-middle">
                      <div className="relative group">
                        <Checkbox
                          checked={selectedStudents.includes(student.email)}
                          onChange={() => handleSelectStudent(student.email)}
                          disabled={student.status === "paused" || student.status === "Opt-out"}
                        />
                        {(student.status === "paused" || student.status === "Opt-out") && (
                          <div className="absolute -top-6 left-0 bg-gray-600 text-white text-xs px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity">
                            Cannot select paused and Opt-out students
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{student.name}</TableCell>
                    <TableCell className="w-32 text-center">{student.BatchChange}</TableCell>
                    <TableCell className="w-32 text-center">
                      <div className="flex justify-center items-center">
                        <span className={getStatusColor(student.status)}>{student.status}</span>
                        <CopyIcon
                          className="h-4 w-4 text-muted-foreground ml-2 cursor-pointer"
                          onClick={() => {
                            navigator.clipboard
                              .writeText(student.email)
                              .then(() => {
                                changeToastVisibility(true)
                                changeToastDetails(STR_SUCCESS, "Email ID Copied successfully")
                              })
                              .catch((err) => {
                                console.error("Failed to copy text: ", err)
                              })
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <Toast data={toast} onClick={() => changeToastVisibility(false)} />
      </div>
    </DashboardLayout>
  )
}
